import { type Node, Fragment, Slice } from '@tiptap/pm/model'
import * as React from 'react'
import ph from 'posthog-js'
import { clsx } from 'clsx'
import * as va from '@vercel/analytics'
import { BubbleMenu, type Editor } from '@tiptap/react'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { getRichTextExtensions } from '~/lib/tiptap/extensions/rich-text'
import {
  BlockLabelMoreActionsPortal,
  useBlockInputBaseProps,
} from '../block-wrapper'
import { getId } from '@basehub/utils/id/index'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'
import { RichTextStoreProvider, useRichTextStore } from './store'
import {
  getImageDimensions,
  extractVideoThumbnailAndDimensions,
  startImageUpload,
  uploadAndReplaceImages,
} from '~/lib/tiptap/plugins/upload-image'
import { List } from '../slash-command'
import { startVideoUpload } from '~/lib/tiptap/plugins/upload-video'
import { invariant } from '@epic-web/invariant'
import { EditorView, type EditorProps } from '@tiptap/pm/view'
import {
  Block,
  RootBlock,
  getBlockUpdateOverride,
  getValueBasedOnSelectedVariants,
  isInstanceBlock,
  type ComponentBlock,
  type InstanceBlock,
  type RichTextBlock,
} from '@basehub/data/replicache/index'
import { useTabsStore } from '../../tabs/store'
import { normalizeStringPath } from '@basehub/data/replicache/index'
import { getInstancesInRichTextById } from '@basehub/tiptap-api-utils/rich-text-helpers'
import { find } from 'linkifyjs'

import { productionR2Origin } from '@basehub/shared-constants'
import { useSlashCommandStore } from '~/lib/tiptap/extensions/rich-text/extensions/slash-command'
import { getRichTextSlashCommands } from './slash-commands'
import { useValidInstancesForBlock } from '~/hooks/use-valid-instances-for-block'
import {
  PrimitiveTipTapEditor,
  PrimitiveTipTapEditorProps,
} from '~/lib/tiptap/components/primitive-editor'
import { useAntiLayoutShift } from '~/hooks/use-anti-layout-shift'
import { Button } from '~/sotano-kit/primitives/button'
import { toast } from 'sonner'
import { useDebouncedCallback } from '~/hooks/use-debounced-callback'
import { GetReferenceClientRect } from 'tippy.js'
import { BasehubActionsMenu } from '~/lib/tiptap/extensions/rich-text/extensions/hover-marks-plugin/basehub-actions'
import { CustomBubbleMenu } from '~/lib/tiptap/components/bubble-menu'
import { MenuBar } from '~/lib/tiptap/components/menu-bar'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { EnterFullScreenIcon, PlusIcon } from '@radix-ui/react-icons'
import { useAllowedFormattingLint } from '~/lib/tiptap/extensions/rich-text/extensions/linter/rules/allowed-formatting'
import { useCopyAssistant } from '~/app/api/ai/copy-assistant/client'
import { isHotkey } from 'is-hotkey'
import { commandKeyPressed } from '~/lib/command-key-pressed'
import {
  useBlockAwareness,
  useCollaborationData,
} from '~/hooks-data/use-repo-presence'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useEditorStore } from '../../editor-store'
import {
  RepoUndoOps,
  useRepoUndoManager,
} from '~/replicache/frontend/undo-redo/manager'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import { useEditorMode } from '../../editor-mode-provider'
import './rt-editor.scss'
import { useEditorWidth } from '~/hooks/use-editor-width'
import {
  RichTextProvider,
  useFindAndReplaceState,
  useRichTextContext,
} from './context'

import { FindAndReplaceToolbar } from './find-and-replace-toolbar'
import { BlockVariant } from '..'
import {
  PASTE_PRIORITIES,
  PasteHandler,
} from '~/lib/tiptap/extensions/rich-text/extensions/paste-priority-handler'
import { transformBasehubBlockPasted } from '~/lib/tiptap/extensions/rich-text/extensions/basehub-block'
import type { Editor as TiptapEditor } from '@tiptap/core'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { rIC } from '~/lib/request-idle-callback'
import { Bold } from '@basehub/tiptap-api-utils'
import { Link } from '@tiptap/extension-link'
import { getAllowedFormatting } from './_utils'
import { useTreeSlice } from '~/hooks/use-tree-slice'

type RichTextBlockInputProps = {
  block: RichTextBlock
  path: string
  parent: Block
  fullSize?: boolean
  readOnly?: boolean
  variant?: BlockVariant
}

export const RichTextBlockInput: React.FC<RichTextBlockInputProps> = (
  props
) => {
  return (
    <RichTextAiProvider
      blockId={props.block.id}
      context={{
        blockTitle: props.block.title,
        documentTitle: 'Document title', // TODO! get document title
      }}
    >
      <RichTextStoreProvider>
        <RichText {...props} />
      </RichTextStoreProvider>
    </RichTextAiProvider>
  )
}

/**
 * This component is isolated so we can declare the
 * handleSelectBlock func inside the RichTextStoreProvider.
 * This is necessary because we need to access the store
 */

const RichText: React.FC<RichTextBlockInputProps> = ({
  block,
  path,
  fullSize,
  readOnly,
  parent,
  variant = 'default',
}) => {
  const { data: selectedTeamAndRepo } = useSelectedTeamAndRepo()
  const { data: validInstancesForBlockData } = useValidInstancesForBlock({
    blockId: block.id,
    dependencies: block.constraints.allowedComponents,
  })
  const editorMode = useEditorMode()
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const baseProps = useBlockInputBaseProps(block)
  const blockTreeManager = useWorkingTreeStore((s) => s.tree)
  const treeRootId = useWorkingTreeStore((s) => s.rootBlockId)
  const rootBlockLastRevertedAt = useTreeSlice(
    blockTreeManager,
    (t) => (t.root.value as RootBlock).lastRevertedAt
  )
  const setRecentlyCreatedBlock = useRichTextStore(
    (s) => s.setRecentlyCreatedBlock
  )
  const [isLinkModalOpen, setIsLinkModalOpen] = React.useState(false)
  const teamId = data?.team.id
  const repoId = data?.repo.id

  const isSingleLineBlock = React.useMemo(
    () => block.constraints.layout === 'single-line',
    [block.constraints.layout]
  )

  const isReadOnly = React.useMemo(
    () => readOnly || editorMode === 'read',
    [readOnly, editorMode]
  )

  const handleSelectBlock = React.useCallback(
    async (targetBlock: ComponentBlock) => {
      if (!replicache) throw new Error('no replicache')
      const newBlockId = getId()
      const { newBlock } = await replicache.mutate.createBlock({
        position: { type: 'last' },
        data: {
          id: newBlockId,
          title: '',
          type: 'instance',
          typeOptions: {
            targetBlockId: idToSourceBlockId(targetBlock),
          },
        },
        parentInstanceChildTarget: block.instanceChildTarget,
        path: normalizeStringPath({
          path,
          format: 'string',
          includeEdge: true,
          edgeId: block.id,
        }),
        isoNow: new Date().toISOString(),
        idSeed: getId(),
      })
      setRecentlyCreatedBlock(newBlock as InstanceBlock)

      va.track('Block created', {
        source: 'Rich text',
        blockType: 'instance',
      })
      ph.capture('Block created', {
        source: 'Rich text',
        blockType: 'instance',
      })

      return newBlock as InstanceBlock
    },
    [replicache, path, setRecentlyCreatedBlock, block]
  )

  const getFileHandler = React.useCallback(() => {
    return {
      priority: PASTE_PRIORITIES.FILE,
      handler: ({
        view,
        event: _,
        slice,
      }: {
        view: EditorView
        event: ClipboardEvent
        slice: Slice
      }) => {
        if (!teamId || !repoId) return { handled: false }
        // Handle image nodes in pasted content
        else if (slice.content.childCount > 0) {
          const imagesFromOutside: string[] = []
          const processNodes = (node: Node) => {
            if (
              node.type.name === 'image' &&
              !node.attrs.src.startsWith(productionR2Origin)
            ) {
              imagesFromOutside.push(node.attrs.src)
            }
          }
          slice.content.descendants(processNodes)

          if (imagesFromOutside.length) {
            rIC(() => {
              uploadAndReplaceImages(imagesFromOutside, view, {
                teamId,
                repoId,
              })
            })
          }
        }

        return { handled: false }
      },
    }
  }, [teamId, repoId])

  const childrenKeys = React.useMemo(
    () => blockTreeManager?.getChildrenKeys(idToSourceBlockId(block)) ?? [],
    [block, blockTreeManager]
  )

  const onPasteBshbInstanceNode = (
    editor: TiptapEditor,
    id: string,
    q: number = 0
  ) => {
    // check if there's an instance block with the same id
    invariant(blockTreeManager, 'no block tree manager')

    const currentBlock = blockTreeManager.getNode(block.id, {
      required: true,
    }).value as RichTextBlock
    invariant(currentBlock, 'no current block')

    const node = blockTreeManager.getNode(id)
    const canBePasted = Boolean(
      node &&
        node.value.type === 'instance' &&
        blockTreeManager.getNode(node.value.typeOptions.targetBlockId)
    )
    if (!canBePasted) return false

    const isPastingInSameRichText = childrenKeys.some((childId) => {
      const node = blockTreeManager.getNode(childId)
      return (
        node &&
        isInstanceBlock(node?.value) &&
        (node.value.id === id || node.value.sourceBlockId === id)
      )
    })

    if (isPastingInSameRichText && node) {
      const instancesInRichText = getInstancesInRichTextById(
        JSON.stringify(editor.getJSON()),
        idToSourceBlockId(node.value)
      )
      const isInTiptapNodes = instancesInRichText.length > q
      if (!isInTiptapNodes) return
    }

    const newId = getId()
    // leave the promies floating as this can't have an await
    replicache?.mutate.duplicateBlock({
      duplicatedBlockId: id,
      newBlockId: newId,
      duplicatedComponentStrategy: 'default',
      position: {
        type: 'last',
      },
      path: normalizeStringPath({
        path,
        edgeId: block.id,
        format: 'string',
        includeEdge: true,
      }),
      isoNow: new Date().toISOString(),
      idSeed: getId(),
    })
    return newId
  }

  const defaultHandlers: PasteHandler[] = [
    getFileHandler(),
    {
      priority: PASTE_PRIORITIES.BLOCK,
      handler: ({ editor, event, slice }) => {
        if (event.type !== 'paste' || !editor) {
          return { handled: false }
        }

        // Just transform and apply directly
        let hasBasehubBlocks = false
        transformBasehubBlockPasted(editor, slice, (id: string) => {
          const res = onPasteBshbInstanceNode(editor, id, 0)
          if (res !== false) {
            hasBasehubBlocks = true
          }
          return res
        })

        return { handled: hasBasehubBlocks }
      },
    },
    {
      priority: PASTE_PRIORITIES.HEADING,
      handler: ({ view, slice }) => {
        let hasModifiedContent = false

        const removeMarks = (
          node: Node,
          marksToRemove: string[]
        ): { node: Node; changed?: boolean } => {
          let modifiedContent: typeof node.content | null = null

          if (node.isText) {
            if (
              node.marks.some((mark) => marksToRemove.includes(mark.type.name))
            ) {
              return {
                node: node.type.schema.text(
                  node.text ?? '',
                  node.marks.filter(
                    (mark) => !marksToRemove.includes(mark.type.name)
                  )
                ),
                changed: true,
              }
            } else {
              return { node }
            }
          } else if (node.isBlock || node.isInline) {
            const contentWithoutMarks: Node[] = []
            let changed = false
            node.content.forEach((child) => {
              const result = removeMarks(child, marksToRemove)
              contentWithoutMarks.push(result.node)
              if (result.changed) {
                changed = true
              }
            })
            if (changed) {
              modifiedContent = Fragment.fromArray(contentWithoutMarks)
            }
          }

          return {
            node: node.type.create(
              node.attrs,
              modifiedContent ?? node.content,
              node.marks
            ),
            changed: !!modifiedContent,
          }
        }

        const newContent: Node[] = []
        slice.content.forEach((node) => {
          if (node.type.name === 'heading') {
            const result = removeMarks(node, [Bold.name, Link.name])
            if (result.changed) {
              hasModifiedContent = true
            }
            newContent.push(result.node)
          } else {
            newContent.push(node)
          }
        })

        if (hasModifiedContent) {
          const newSlice = new Slice(
            Fragment.fromArray(newContent),
            slice.openStart,
            slice.openEnd
          )
          const tr = view.state.tr.replaceSelection(newSlice)
          view.dispatch(tr)
          return { handled: true }
        }

        return { handled: false }
      },
    },
    {
      priority: PASTE_PRIORITIES.LINK,
      handler: ({ view, slice, editor }) => {
        const { state } = view
        const { selection } = state
        const { empty } = selection

        if (empty) {
          return { handled: false, preventDefault: false }
        }

        let textContent = ''

        slice.content.forEach((node) => {
          textContent += node.textContent
        })

        const link = find(textContent).find(
          (item) => item.isLink && item.value === textContent
        )

        if (!textContent || !link) {
          return { handled: false, preventDefault: false }
        }

        editor.commands.setLink({
          href: link.href,
          type: 'link',
          target: '_BLANK',
        })

        return { handled: true, preventDefault: true }
      },
    },
  ]

  const editorProps: EditorProps = React.useMemo(
    () => ({
      handlePaste: (view, event, _slice) => {
        const file = event.clipboardData?.files?.[0]
        const pos = view.state.selection.from

        if (file) {
          if (!teamId || !repoId) return true // prevent default even if no upload

          if (file.type.includes('image/')) {
            event.preventDefault()
            getImageDimensions(file).then((dimensions) => {
              startImageUpload(
                file,
                view,
                pos,
                { teamId, repoId },
                {
                  ...(dimensions.width &&
                    dimensions.height && {
                      width: dimensions.width,
                      height: dimensions.height,
                    }),
                }
              )
            })
            return true
          }

          if (file.type.includes('video/')) {
            event.preventDefault()
            extractVideoThumbnailAndDimensions(file).then((dimensions) => {
              startVideoUpload(
                file,
                view,
                pos,
                { teamId, repoId },
                {
                  ...(dimensions.width &&
                    dimensions.height && {
                      width: dimensions.width,
                      height: dimensions.height,
                    }),
                  ...(dimensions.thumbnail && {
                    thumbnail: dimensions.thumbnail,
                  }),
                }
              )
            })
            return true
          }

          return false
        }

        // fallback: if you want to *avoid* default paste for other content too:
        // event.preventDefault()
        // return true

        return false
      },
      handleDrop: (view, event, _slice, moved) => {
        if (!teamId || !repoId) return false
        if (
          !moved &&
          event.dataTransfer &&
          event.dataTransfer.files &&
          event.dataTransfer.files[0]
        ) {
          event.preventDefault()
          const file = event.dataTransfer.files[0]
          const coordinates = view.posAtCoords({
            left: event.clientX,
            top: event.clientY,
          })
          if (!coordinates) return
          // here we deduct 1 from the pos or else the image will create an extra node
          if (file.type.includes('image/')) {
            getImageDimensions(file).then((dimensions) => {
              startImageUpload(
                file,
                view,
                coordinates.pos - 1,
                {
                  teamId,
                  repoId,
                },
                {
                  ...(dimensions.width &&
                    dimensions.height && {
                      width: dimensions.width,
                      height: dimensions.height,
                    }),
                }
              )
            })
          } else {
            extractVideoThumbnailAndDimensions(file).then((dimensions) => {
              startVideoUpload(
                file,
                view,
                coordinates.pos - 1,
                {
                  teamId,
                  repoId,
                },
                {
                  ...(dimensions.width &&
                    dimensions.height && {
                      width: dimensions.width,
                      height: dimensions.height,
                    }),
                  ...(dimensions.thumbnail && {
                    thumbnail: dimensions.thumbnail,
                  }),
                }
              )
            })
          }
          return true
        } else if (_slice && _slice.content.childCount > 0 && !moved) {
          const imageUrls: string[] = []
          _slice.content.descendants((node) => {
            if (node.type.name === 'image') {
              imageUrls.push(node.attrs.src)
            }
          })
          const imagesFromOutside = imageUrls.filter(
            (url) => !url.startsWith(productionR2Origin)
          )
          if (imagesFromOutside.length === 0) return false
          rIC(() => {
            uploadAndReplaceImages(imagesFromOutside, view, {
              teamId,
              repoId,
            })
          })
          return false
        }
        return false
      },
      handleKeyDown(view, event) {
        // cmd + k opens the link modal
        if (event.key === 'k' && commandKeyPressed(event)) {
          const isEmpty = view.state.selection.content().size === 0
          if (isEmpty) return false
          event.preventDefault()
          event.stopPropagation()
          setIsLinkModalOpen((open) => !open)
          return true
        }
        return false
      },
      handleDOMEvents: {
        mousedown: (_view, event) => {
          // Check if event is from inside any of your custom nodes
          const target = event.target as HTMLElement
          if (target.closest('[data-node-view-wrapper]')) {
            return true // Tell ProseMirror: "I handled this, ignore it"
          }
          return false // Let ProseMirror handle everything else normally
        },
        click: (_view, event) => {
          const target = event.target as HTMLElement
          if (target.closest('[data-node-view-wrapper]')) {
            return true
          }
          return false
        },
        keydown: (_view, event) => {
          const target = event.target as HTMLElement
          if (target.closest('[data-node-view-wrapper]')) {
            if (event.key === 'Enter') {
              const slashCommandEnabled = document.querySelector(
                '[data-slash-command-enabled="true"]'
              )
              if (slashCommandEnabled) {
                event.preventDefault()
                return true
              }
            }
            return true
          }
          return false
        },
        // don't add these (they break nested slash command)
        // focus: (view, event) => {
        //   const target = event.target as HTMLElement
        //   if (target.closest('[data-node-view-wrapper]')) {
        //     return true
        //   }
        //   return false
        // },
        // blur: (view, event) => {
        //   const target = event.target as HTMLElement
        //   if (target.closest('[data-node-view-wrapper]')) {
        //     return true
        //   }
        //   return false
        // },
      },
    }),
    [teamId, repoId]
  )

  const addTab = useTabsStore((s) => s.addTab)
  const { triggerCompletion } = useRichTextAiContext()
  const [isMetaPressed, setIsMetaPressed] = React.useState(false)
  const allowedFormatting = getAllowedFormatting(block.constraints)

  // handler for marks have a cursor pointer when meta is pressed
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (commandKeyPressed(e) === false) return
      setIsMetaPressed(true)
    }
    const keyUp = (e: KeyboardEvent) => {
      if (commandKeyPressed(e)) return
      setIsMetaPressed(false)
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('keyup', keyUp)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('keyup', keyUp)
    }
  }, [])

  React.useEffect(() => {
    if (!validInstancesForBlockData?.allComponents || !repoId || !teamId) {
      return
    }
    let commands = getRichTextSlashCommands({
      repoId,
      teamId,
      startImageUpload(file, view, startPos, options) {
        getImageDimensions(file).then((dimensions) => {
          startImageUpload(file, view, startPos, options, {
            ...(dimensions.width &&
              dimensions.height && {
                width: dimensions.width,
                height: dimensions.height,
              }),
          })
        })
      },
      startVideoUpload(file, view, startPos, options) {
        extractVideoThumbnailAndDimensions(file).then((dimensions) => {
          startVideoUpload(file, view, startPos, options, {
            ...(dimensions.width &&
              dimensions.height && {
                width: dimensions.width,
                height: dimensions.height,
              }),
            ...(dimensions.thumbnail && {
              thumbnail: dimensions.thumbnail,
            }),
          })
        })
      },
      triggerCompletion: (editor) => {
        triggerCompletion(editor as Editor)
      },
    })

    if (allowedFormatting.length > 0) {
      commands = commands.map((c) => ({
        ...c,
        commands: c.commands.filter((command) =>
          command.tipTapKey
            ? allowedFormatting.includes(command.tipTapKey)
            : true
        ),
      }))
    }
    useSlashCommandStore.setState({
      components: validInstancesForBlockData.allComponents,
      commands,
    })
  }, [
    validInstancesForBlockData?.allComponents,
    repoId,
    teamId,
    allowedFormatting,
    triggerCompletion,
  ])

  const selectedDashboardVariants = useSelectedVariants({
    parentBlock: parent,
    block,
  })

  const value = {
    // variants don't have children nor transformations
    ...block.value,
    ...getValueBasedOnSelectedVariants({
      block,
      variantsToFilterBy: selectedDashboardVariants,
    }),
  }

  const handleUpdate = React.useCallback(
    async (update: string) => {
      const batchedUpdate: UpdateBlockParams = {
        id: block.id,
        instanceChildTarget: block.instanceChildTarget,
        data: getBlockUpdateOverride({
          block,
          newValue: {
            state: update,
          },
          selectedVariants: selectedDashboardVariants,
        }),
        path,
        strategy: 'merge-deep',
        isoNow: new Date().toISOString(),
      }

      // Use React's automatic batching
      replicache?.mutate.updateBlock(batchedUpdate)
    },
    [replicache?.mutate, block, selectedDashboardVariants, path]
  )
  // const handleUpdate = useDebouncedCallback(
  //   async (update: string) => {
  //     await replicache?.mutate.updateBlock({
  //       id: block.id,
  //       data: getBlockUpdateOverride({
  //         block,
  //         newValue: { state: update },
  //         selectedVariants: selectedDashboardVariants,
  //       }),
  //       path,
  //       strategy: 'merge-deep',
  //       isoNow: new Date().toISOString(),
  //     })
  //   },
  //   // Array of dependencies
  //   [replicache?.mutate, block, selectedDashboardVariants, path],
  //   // Delay in ms - adjust this value based on typing performance
  //   100 // or try 150-200ms if still laggy
  // )

  // const [localContent, setLocalContent] = React.useState(block.value.state)

  // // Handler for immediate UI updates
  // const handleLocalUpdate = (update: string) => {
  //   setLocalContent(update) // Update UI immediately
  //   handleUpdate(update) // Debounced server update
  // }

  const { setSearchText, ...findAndReplaceState } = useFindAndReplaceState()
  return (
    <RichTextProvider
      value={{
        isLinkModalOpen,
        setIsLinkModalOpen,
        setSearchText,
        ...findAndReplaceState,
      }}
    >
      <TipTapRichText
        {...baseProps}
        instanceId={`rt-branch-${treeRootId}-${
          // intentionally not using sourceBlockId cause we want this to re-initialize the ydoc
          // focus restoration will be handled inside
          block.id
        }/${selectedDashboardVariants.map((i) => i.variantId).join('-')}-${value.lastRevertedAt}-${rootBlockLastRevertedAt}`}
        focusRestorationId={idToSourceBlockId(block)}
        key={
          block.createdAt +
          block.id +
          value.lastRevertedAt + // discarded changes should re-initialize all local state so that yjs doesn't merge stuff
          rootBlockLastRevertedAt + // discarded changes should re-initialize all local state so that yjs doesn't merge stuff
          selectedDashboardVariants.map((i) => i.variantId).join('-')
        }
        block={block}
        blockHash={block.id}
        readOnly={isReadOnly}
        marksController
        storeSuffix={fullSize ? 'full-size' : 'blocked'}
        editorProps={editorProps}
        path={path}
        clientId={replicache?.clientID}
        className={clsx(
          'rt-editor',
          isMetaPressed && 'meta-pressed',
          fullSize && variant === 'default' ? 'full-size' : 'blocked',
          variant !== 'default' && 'rt-variant-nested'
        )}
        editorStyle={{
          minHeight: isSingleLineBlock ? 'min-content' : undefined,
        }}
        allowedFormatting={allowedFormatting}
        getExtensions={({ ...collab }) => {
          return getRichTextExtensions({
            collaborationExtensionsOptions: collab,
            linterExtensionOptions: {
              allowedFormatting: allowedFormatting,
            },
            slashCommand: {
              component: List,
              onSelectBlock: handleSelectBlock,
              richTextId: block.id,
            },
            onClickBshbInstanceNode(id) {
              addTab({ key: id, partiallyOpen: true })
            },
            pasteHandlers: defaultHandlers,
            redirect: (url: string) => {
              return addTab({ key: url, partiallyOpen: true })
            },
          })
        }}
        value={value.state}
        ai={{
          context: {
            documentTitle: parent.title ?? '',
            repositoryTitle: selectedTeamAndRepo?.repo?.title ?? '',
            blockTitle: block.title,
          },
        }}
        onUpdate={handleUpdate}
        bubbleMenu={!isReadOnly}
        findAndReplace={!isReadOnly}
        alwaysShowPlaceholder
      />
    </RichTextProvider>
  )
}

export type TipTapRichTextProps = PrimitiveTipTapEditorProps & {
  block: RichTextBlock
  blockHash: string
  storeSuffix: string
  bubbleMenu?: boolean
  ai?: {
    context: {
      documentTitle: string
      repositoryTitle: string
      blockTitle?: string
    }
  }
  path: string
  marksController?: boolean
  allowedFormatting?: string[]
  style?: React.CSSProperties
  editorStyle?: React.CSSProperties
  findAndReplace?: boolean
}

const TipTapRichText = ({
  block,
  blockHash,
  storeSuffix,
  bubbleMenu,
  ai: _ai,
  path,
  marksController,
  allowedFormatting,
  className,
  style,
  editorStyle,
  findAndReplace,
  clientId,
  ...rest
}: TipTapRichTextProps) => {
  const { ref, antiLayoutShiftStyle, updateHeight } =
    useAntiLayoutShift<HTMLDivElement>({
      blockId: block.id,
      storeSuffix,
      initialValue: '100%',
    })
  const addTab = useTabsStore((s) => s.addTab)
  const undoManager = useRepoUndoManager()

  const [editor, setEditor] = React.useState<Editor | null>(null)

  /* ------------------- ALLOWED FORMATTING ------------------- */
  useAllowedFormattingLint({ editorElement: editor?.view.dom, blockHash })

  const editorRef = React.useRef(editor)
  editorRef.current = editor

  React.useEffect(() => {
    if (allowedFormatting && editorRef.current) {
      const newTransaction = editorRef.current.state.tr
      newTransaction.setMeta('allowedFormatting', allowedFormatting)
      editorRef.current.view.dispatch(newTransaction)
    }
  }, [allowedFormatting])

  /* ------------------- AI CONTINUE WRITING ------------------- */

  /* 
  /* ------------------- AI COMPLETION ------------------- */
  // const [isWriting, setIsWriting] = React.useState(false)
  // const {
  //   complete: predict,
  //   completion: prediction,
  //   setCompletion: setPrediction,
  //   stop: stopPrediction,
  //   isLoading: isPredicting,
  // } = useCopyAssistant({ stream: true, enabled: COPILOT_ENABLED })

  // // trigger prediction
  // const triggerPrediction = React.useCallback(
  //   (force?: boolean) => {
  //     if (
  //       !editor ||
  //       !editor.isFocused ||
  //       (!isWriting && force !== true) ||
  //       !COPILOT_ENABLED
  //     ) {
  //       return
  //     }

  //     if (isPredicting) stopPrediction()
  //     if (editor.getText().length < 10) {
  //       setPrediction('')
  //       return
  //     }

  //     predict(getPrevsParagraphs(editor, { count: 2 }) ?? '', {
  //       context: {
  //         pageTitle: ai?.context.documentTitle ?? '',
  //         blockTitle: ai?.context.blockTitle ?? '',
  //         blockCurrentValue: editor.storage.markdown.getMarkdown(),
  //       },
  //       type: 'auto-complete',
  //     })
  //   },
  //   [
  //     ai?.context.blockTitle,
  //     ai?.context.documentTitle,
  //     editor,
  //     predict,
  //     isPredicting,
  //     stopPrediction,
  //     setPrediction,
  //     isWriting,
  //   ]
  // )

  // debounced trigger prediction
  // const triggerPredictionRef = React.useRef(triggerPrediction)
  // triggerPredictionRef.current = triggerPrediction
  // const triggerPredictionDebounced = useDebouncedCallback(
  //   () => triggerPredictionRef.current(),
  //   [],
  //   900
  // )

  // Set prediction to extension suggestion and add retriggers
  // React.useEffect(() => {
  //   if (!editor || !COPILOT_ENABLED) return
  //   const extension = editor.extensionManager.extensions.find(
  //     (ext) => ext.name === Autocomplete.name
  //   )
  //   if (!extension) return
  //   const view = editor.view as EditorView
  //   const onInput = () => {
  //     if (extension) extension.options.suggestion = ''
  //     if (!isWriting) {
  //       setIsWriting(true)
  //     }
  //     triggerPredictionDebounced()
  //   }
  //   const onSelection = () => {
  //     if (extension) extension.options.suggestion = ''
  //     triggerPredictionDebounced()
  //   }
  //   const onBlur = () => {
  //     if (extension) extension.options.suggestion = ''
  //     stopPrediction()
  //   }

  //   // on change selection, trigger prediction
  //   view.dom.addEventListener('input', onInput)
  //   editor.on('selectionUpdate', onSelection)
  //   editor.on('blur', onBlur)
  //   return () => {
  //     view.dom.removeEventListener('input', onInput)
  //     editor.off('selectionUpdate', onSelection)
  //     editor.off('blur', onBlur)
  //   }
  // }, [editor, triggerPredictionDebounced, isWriting, stopPrediction])

  // React.useEffect(() => {
  //   if (!COPILOT_ENABLED) return
  //   const extension = editorRef.current?.extensionManager.extensions.find(
  //     (ext) => ext.name === Autocomplete.name
  //   )

  //   if (!extension) return
  //   extension.options['suggestion'] = prediction
  //   if (editorRef.current) {
  //     editorRef.current.view.dispatch(editorRef.current.state.tr)
  //   }
  // }, [prediction])

  // React.useEffect(() => {
  //   if (!COPILOT_ENABLED) return
  //   if (!editor) return
  //   const onKeyDown = (e: KeyboardEvent) => {
  //     if (!editor.isFocused) return
  //     if (isWriting && e.key === 'Escape') {
  //       stopPrediction()
  //       setIsWriting(false)
  //       const ext = editor.extensionManager.extensions.find(
  //         (ext) => ext.name === Autocomplete.name
  //       )
  //       if (ext) ext.options.suggestion = ''
  //       editor.view.dispatch(editor.state.tr)
  //       e.stopPropagation()
  //       return
  //     }
  //   }
  //   const off = () => setIsWriting(false)

  //   // on keydown set isWritting to true
  //   // on mouseup set isWritting to false
  //   // on blur set isWritting to false

  //   editor.view.dom.addEventListener('keydown', onKeyDown)
  //   editor.view.dom.addEventListener('mousedown', off)
  //   editor.view.dom.addEventListener('blur', off)

  //   return () => {
  //     editor.view.dom.removeEventListener('keydown', onKeyDown)
  //     editor.view.dom.removeEventListener('mousedown', off)
  //     editor.view.dom.removeEventListener('blur', off)
  //   }
  // }, [editor, triggerPredictionDebounced, isWriting, stopPrediction])

  /* ------------------- AI Shortcuts ------------------- */
  // const nextPrediction = React.useCallback(
  //   async (e: KeyboardEvent) => {
  //     if (!editorRef.current || !COPILOT_ENABLED) return
  //     if (isHotkey('ctrl+]', e) && editorRef.current.getText().length > 10) {
  //       await predict(
  //         getPrevsParagraphs(editorRef.current, { count: 2 }) ?? '',
  //         {
  //           context: {
  //             pageTitle: ai?.context.documentTitle ?? '',
  //             blockTitle: ai?.context.blockTitle ?? '',
  //             blockCurrentValue: editorRef.current.getText(),
  //           },
  //           // prevResponse: prediction, TODO
  //           type: 'auto-complete',
  //         }
  //       )
  //     } else if (isPredicting && e.ctrlKey && e.key === ' ') {
  //       e.preventDefault()
  //       e.stopPropagation()
  //     } else if (!isWriting && e.ctrlKey && e.key === ' ') {
  //       e.preventDefault()
  //       e.stopPropagation()
  //       setIsWriting(true)
  //       triggerPredictionRef.current(true)
  //     }
  //   },
  //   [
  //     ai?.context.blockTitle,
  //     ai?.context.documentTitle,
  //     predict,
  //     isWriting,

  //     isPredicting,
  //   ]
  // )

  // React.useEffect(() => {
  //   if (!COPILOT_ENABLED) return
  //   editorRef.current?.view.dom.addEventListener('keydown', nextPrediction)

  //   return () => {
  //     editorRef.current?.view.dom.removeEventListener('keydown', nextPrediction)
  //   }
  // }, [nextPrediction])

  /* ------------------- LAYOUT SHIFT ------------------- */

  const handleUpdateAntiLayoutShiftHeight = useDebouncedCallback(
    () => {
      if (!ref.current) return
      // select the html node that has data-rich-text-block="true"
      const editorEl = ref.current.querySelector(
        '[data-rich-text-block="true"]'
      )
      if (!editorEl) return
      const height = editorEl.getBoundingClientRect().height
      updateHeight(height)
    },
    [ref, updateHeight],
    !antiLayoutShiftStyle.height ? 0 : 500
  )

  // update lastObservedHeight when the editor state changes
  React.useEffect(() => {
    if (!editor) return

    editor.on('update', handleUpdateAntiLayoutShiftHeight)

    return () => {
      editor.off('update', handleUpdateAntiLayoutShiftHeight)
    }
  }, [editor, handleUpdateAntiLayoutShiftHeight])

  /* ---------------   FULLSCREEN TABLES ---------------- */

  const editorWidth = useEditorWidth()
  React.useEffect(() => {
    if (!editorWidth) return
    const proseMirror = editor?.view.dom
    if (!proseMirror) return
    if (
      ref.current &&
      'attributeStyleMap' in ref.current &&
      storeSuffix === 'full-size'
    ) {
      // if the editor is full size, we need to check if the editor is wider than the screen
      const margin = editorWidth - proseMirror.getBoundingClientRect().width / 2
      ref.current.attributeStyleMap.set('--table-margin', `${margin}px`)
    }
  }, [editorWidth, editor, storeSuffix, ref])

  /* ------------------- BUBBLE MENU ------------------- */
  const [isShowingBubbleMenu, setIsShowingBubbleMenu] = React.useState(false)

  const GetTableRowCoords: GetReferenceClientRect = React.useCallback(() => {
    const view = editor?.view as EditorView
    const pos = view?.state.selection.$from
    let depth = pos.depth
    while (depth > 1) {
      if (pos.node(depth).type.name == 'tableRow') break
      depth--
    }
    const from = pos.before(depth)
    const nodeDom = view.nodeDOM(from) as HTMLElement | null
    if (!nodeDom) return new DOMRect(0, 0, 0, 0)
    const rect = nodeDom.getBoundingClientRect()
    return new DOMRect(rect.x, rect.y, rect.width, rect.height)
  }, [editor])

  const getTableColumnCoords: GetReferenceClientRect = React.useCallback(() => {
    const view = editor?.view as EditorView
    const pos = view.state.selection.$from
    let depth = pos.depth,
      cellDepth = 0,
      tableDepth = 0
    while (depth > 0) {
      if (
        pos.node(depth).type.name == 'tableCell' ||
        pos.node(depth).type.name == 'tableHeader'
      ) {
        cellDepth = depth
      }
      if (pos.node(depth).type.name == 'table') {
        tableDepth = depth
        break
      }
      depth--
    }
    if (!(tableDepth && cellDepth)) {
      return new DOMRect(0, 0, 0, 0)
    }
    const cellNode = view.nodeDOM(pos.before(cellDepth)) as HTMLElement
    const tableNode = view.nodeDOM(pos.before(tableDepth)) as HTMLElement
    const cellRect = cellNode.getBoundingClientRect()
    const tableRect = tableNode.getBoundingClientRect()

    return new DOMRect(
      cellRect.x,
      tableRect.y,
      cellRect.width,
      tableRect.height
    )
  }, [editor])

  // ---------------------------------------------------------------------------
  // Find and Replace
  // ---------------------------------------------------------------------------

  const {
    setFindAndReplaceState: setState,
    setSearchText,
    searchInputRef,
  } = useRichTextContext()

  React.useEffect(() => {
    const ac = new AbortController()
    editor?.view.dom.addEventListener(
      'keydown',
      (e) => {
        if (isHotkey('mod+f', e)) {
          e.preventDefault()
          setState('searching')
          const currentSelection = editor?.view.state.selection
          const text = editor?.view.state.doc.textBetween(
            currentSelection.from,
            currentSelection.to,
            '\n'
          )
          if (text.trim().length > 0) {
            setSearchText(text)
          }
          setTimeout(() => {
            searchInputRef.current?.focus()
          }, 50)
        }
      },
      { signal: ac.signal }
    )

    return () => {
      // cleanup
      ac.abort()
    }
  }, [editor, setState, setSearchText, searchInputRef])

  const optimisticUser = useCollaborationData()
  const awarenessList = useBlockAwareness(block.id)

  const setSelectedBlockAwareness = useEditorStore(
    (s) => s.setSelectedBlockAwareness
  )
  const isSelected = useEditorStore(
    (s) => s.selectedBlockId === idToSourceBlockId(block)
  )

  const blockAwareness = {
    blockId: block.id,
    isSelected,
    setSelectedBlockAwareness,
    remoteAwarenessList: awarenessList,
  }

  const activeTabKey = useTabsStore((s) => s.activeTab?.key)

  return (
    <div ref={ref} style={{ ...style }} className="flex flex-col shrink-0">
      {editor && findAndReplace && <FindAndReplaceToolbar editor={editor} />}
      {editor && marksController && (
        <div>
          <BasehubActionsMenu editor={editor} />
        </div>
      )}
      {editor && bubbleMenu && (
        <div>
          <CustomBubbleMenu
            editor={editor}
            blockId={block.id}
            isShowingBubbleMenu={isShowingBubbleMenu}
            setIsShowingBubbleMenu={setIsShowingBubbleMenu}
          >
            <MenuBar
              editor={editor}
              block={block}
              path={path}
              isShowingBubbleMenu={isShowingBubbleMenu}
            />
          </CustomBubbleMenu>
          <BubbleMenu
            shouldShow={({ editor }) => {
              return editor.isActive('table')
            }}
            editor={editor}
            tippyOptions={{
              arrow: false,
              placement: 'right',
              getReferenceClientRect: GetTableRowCoords,
              offset: [0, 0],
            }}
          >
            <IconButton
              className="bg-dark-shade border-dark-border-solid border"
              type="button"
              onClick={() => {
                editor.commands.addColumnAfter()
              }}
            >
              <PlusIcon className="size-2" />
            </IconButton>
          </BubbleMenu>
          <BubbleMenu
            shouldShow={({ editor }) => {
              return editor.isActive('table')
            }}
            editor={editor}
            tippyOptions={{
              arrow: false,
              placement: 'bottom',
              getReferenceClientRect: getTableColumnCoords,
              offset: [0, -2],
            }}
          >
            <IconButton
              className="bg-dark-shade border-dark-border-solid border"
              type="button"
              onClick={() => {
                editor.commands.addRowAfter()
              }}
            >
              <PlusIcon className="size-2" />
            </IconButton>
          </BubbleMenu>
        </div>
      )}

      <div className="flex flex-col shrink-0">
        <PrimitiveTipTapEditor
          {...rest}
          style={{ ...antiLayoutShiftStyle, ...editorStyle }}
          user={optimisticUser}
          clientId={clientId}
          blockAwareness={blockAwareness}
          undo={{
            focusOp: {
              op: 'focus',
              args: {
                preferredTabKey: activeTabKey,
                focus: {
                  type: 'block:value',
                  blockId: idToSourceBlockId(block),
                },
              },
            } satisfies RepoUndoOps[number],
            manager: undoManager.current,
            persist: 'sessionStorage',
          }}
          className={clsx('flex flex-col shrink-0', className)}
          setEditor={setEditor}
        />
      </div>

      <BlockLabelMoreActionsPortal blockId={block.id}>
        {/* <Button
          unstyled
          tabIndex={-1}
          focusStyled
          onClick={() =>
            // complete writing with AI
            triggerCompletion()
          }
          className="mr-2 flex cursor-default items-center space-x-1 text-[13px] leading-4 text-dark-muted !outline-dark-border-solid hover:text-dark-dim"
        >
          Complete writing with AI
          <AIIcon width={16} height={16} />
        </Button> */}
        {block.constraints.layout !== 'single-line' && (
          <Button
            unstyled
            tabIndex={-1}
            focusStyled
            onClick={() => addTab({ key: block.id, partiallyOpen: true })}
            className="mr-2 flex cursor-default items-center space-x-1 text-[13px] leading-4 text-dark-muted !outline-dark-border-solid hover:text-dark-dim"
          >
            <span>Open full editor</span>
            <EnterFullScreenIcon width={16} height={16} />
          </Button>
        )}
      </BlockLabelMoreActionsPortal>
    </div>
  )
}

const getPrevText = (
  editor: Editor,
  {
    chars,
    offset = 0,
  }: {
    chars: number
    offset?: number
  }
) => {
  return editor.state.doc.textBetween(
    Math.max(0, editor.state.selection.from - chars),
    editor.state.selection.from - offset,
    '\n'
  )
}

// const getPrevsParagraphs = (
//   editor: Editor,
//   {
//     count = 1,
//   }: {
//     count?: number
//   }
// ) => {
//   // get the past n paragraphs
//   const text = editor.getText()
//   const sliced = text.split('\n').slice(0, editor.state.selection.from)
//   const paragraphs = sliced.filter((s) => s.trim() !== '')
//   return paragraphs.slice(-count).join('\n')
// }

/* -------------------------------------------------------------------------- */
/*                                AI CONTEXT ✨                                */
/* -------------------------------------------------------------------------- */

interface AiContext {
  setEditor: (editor: Editor) => void
  triggerCompletion: (editor: Editor) => void
}
const richTextAiContext = React.createContext<AiContext | null>(null)

const useRichTextAiContext = () =>
  React.useContext(richTextAiContext) as AiContext

const RichTextAiProvider = ({
  children,
  context,
}: {
  children: React.ReactNode
  context: {
    documentTitle: string
    blockTitle: string
  }
  blockId: string
}) => {
  const [editor, setEditor] = React.useState<Editor | null>(null)
  const prev = React.useRef('')

  const { complete, completion, isLoading, stop, setCompletion } =
    useCopyAssistant({
      onFinish: (_, completion) => {
        if (!editor) return
        editor?.commands.setTextSelection({
          from: editor?.state.selection.from - completion.length,
          to: editor?.state.selection.from,
        })
        setEditor(null)
        setCompletion('')
      },
      stream: true,
    })

  const triggerCompletion = React.useCallback(
    (editor: Editor) => {
      setEditor(editor)
      if (isLoading) stop()
      prev.current = ''
      if (!editor) return
      editor.commands.focus()
      complete(getPrevText(editor, { chars: 2000 }) ?? '', {
        context: {
          pageTitle: context.documentTitle ?? '',
          blockTitle: context.blockTitle ?? '',
          blockCurrentValue: editor.getText(),
        },
        type: 'continue-writing',
      })
    },
    [context, complete, isLoading, stop]
  )

  React.useEffect(() => {
    if (!editor || !completion) return
    const diff = completion?.slice(prev.current.length) ?? ''
    diff.split('\n').forEach((_diff) => {
      if (_diff === '') {
        editor!.commands.insertContent('<p></p>')
        return
      }
      try {
        editor!.commands.insertContent(_diff.toString())
      } catch (e) {
        console.error(e)
      }
    })
    if (prev) prev.current = completion
  }, [completion, editor])

  // Set keydown event for AI completion pause
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || (commandKeyPressed(e) && e.key === 'z')) {
        stop()
        if (e.key === 'Escape') {
          editor?.commands.deleteRange({
            from:
              editor.state.selection.from -
              (completion ? completion.length : 0),
            to: editor.state.selection.from,
          })
        }
      }
    }

    const mousedownHandler = (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      stop()
      const t = toast(
        <div className="flex w-full items-center justify-between">
          <p>AI completion paused</p>
          <Button
            size="small"
            onClick={() => {
              if (editor) triggerCompletion(editor)
              toast.dismiss(t)
            }}
          >
            Continue
          </Button>
        </div>
      )
    }

    if (isLoading) {
      document.addEventListener('keydown', onKeyDown)
      window.addEventListener('mousedown', mousedownHandler)
    } else {
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('mousedown', mousedownHandler)
    }
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('mousedown', mousedownHandler)
    }
  }, [isLoading, editor, completion, triggerCompletion, stop])

  return (
    <richTextAiContext.Provider
      value={{
        setEditor,
        triggerCompletion,
      }}
    >
      {children}
    </richTextAiContext.Provider>
  )
}
